import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Phone, 
  ArrowRight, 
  Shield, 
  Wrench, 
  Users, 
  Heart,
  CheckCircle
} from "lucide-react";

const PHONE_NUMBER = "(940) 337-9761";
const PHONE_LINK = "tel:+19403379761";
const SHOP_IMAGE = "https://customer-assets.emergentagent.com/job_trusted-car-service/artifacts/fns13x69_IMG_1989.jpeg";
const TEAM_IMAGE = "https://images.pexels.com/photos/7019606/pexels-photo-7019606.jpeg";

const values = [
  { 
    icon: Shield, 
    title: "Honest & Transparent", 
    description: "No hidden fees or surprise charges. We explain everything before we start any work." 
  },
  { 
    icon: Wrench, 
    title: "Quality Workmanship", 
    description: "We stand behind our work with warranties and a commitment to doing it right." 
  },
  { 
    icon: Users, 
    title: "Customer First", 
    description: "Your satisfaction is our priority. We treat every vehicle like it's our own." 
  },
  { 
    icon: Heart, 
    title: "Community Focused", 
    description: "We're proud to serve Wichita Falls and surrounding communities." 
  },
];

const commitments = [
  "Fair and upfront pricing on all services",
  "Quality parts and fluids for lasting repairs",
  "Experienced, certified mechanics",
  "Quick turnaround without cutting corners",
  "Clear communication throughout the process",
  "Friendly, family-oriented service",
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in-up">
              <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-foreground">
                About <span className="text-primary">Tony's Auto</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Tony's Auto Quality Service is a family-owned and operated auto repair shop 
                located in Wichita Falls, Texas. We've built our reputation on honest work, 
                fair prices, and treating every customer like family.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                When you bring your vehicle to us, you can trust that we'll diagnose the 
                problem accurately, explain your options clearly, and only recommend the 
                repairs you actually need. That's the Tony's difference.
              </p>
            </div>
            <div className="relative animate-fade-in-up delay-200">
              <img
                src={SHOP_IMAGE}
                alt="Tony's Auto Shop"
                className="rounded-lg shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-white p-6 rounded-lg shadow-xl">
                <p className="font-heading text-4xl">4.8</p>
                <p className="text-sm text-blue-100">Star Rating</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section data-testid="our-story" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative animate-fade-in-up">
              <img
                src={TEAM_IMAGE}
                alt="Our Team"
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6 animate-fade-in-up delay-100">
              <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-foreground">
                Our <span className="text-primary">Story</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                What started as a passion for fixing cars has grown into a trusted local 
                business serving the Wichita Falls community. We believe in old-fashioned 
                values: honesty, hard work, and standing behind what we do.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Our team of experienced mechanics brings years of expertise to every job, 
                big or small. We invest in the latest diagnostic equipment and ongoing 
                training to ensure we can handle modern vehicles while maintaining the 
                personal touch that sets us apart.
              </p>
              <div className="pt-4">
                <Link to="/contact" data-testid="about-contact-btn">
                  <Button className="cta-secondary flex items-center gap-2">
                    Come Visit Us
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section data-testid="our-values" className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-foreground mb-4">
              Our <span className="text-primary">Values</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              These principles guide everything we do at Tony's Auto Quality Service.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-center space-y-4 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl uppercase text-foreground">
                  {value.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section data-testid="our-commitment" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-foreground mb-8">
              Our <span className="text-primary">Commitment</span> to You
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-12">
              {commitments.map((commitment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 animate-fade-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground">{commitment}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PHONE_LINK} data-testid="about-call-btn">
                <Button className="cta-primary flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  {PHONE_NUMBER}
                </Button>
              </a>
              <Link to="/quote" data-testid="about-quote-btn">
                <Button className="cta-secondary flex items-center gap-2">
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
